import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import validEmailRegEx from 'constants/validations';
import { useMutation } from '@apollo/client';
import { ADD_OR_UPDATE_COMMENT_MUTATION } from '../../mutations';
import { useToast } from 'utils/hooks';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { DOCUMENT_COMMENT_ADDED, DOCUMENT_COMMENT_UPDATED } from '../../subscriptions';
import { shallowEqual, useSelector } from 'react-redux';
import AddCommentComponent from './AddCommentComponent';
import { defaultComment } from '../../constants/defaultComment';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { COMMENTS_USER_AGENDA_QUERY } from '../../queries';
import { useStateLens } from '@totalsoft/react-state-lens';
import { useValidation } from '@totalsoft/pure-validations-react';
import { required, shape } from '@totalsoft/pure-validations';

const commentValidator = shape({
  commentText: required
});

function AddCommentContainer({
  documentId,
  siteId,
  comment,
  setComment,
  fullScreen,
  canSwitch,
  refetchComments,
  inputRef,
  dirtyComment,
  setDirtyComment
}) {
  const addToast = useToast();
  const userInfo = useSelector(state => state.userInfo, shallowEqual);
  const [commentLens, , resetCommentLens] = useChangeTrackingLens(defaultComment);
  const [saving, setSaving] = useState(false);
  const currentComment = commentLens |> get;
  const userAgendaLens = useStateLens([]);
  const [commentValidation, validateComment] = useValidation(commentValidator);

  useQueryWithErrorHandling(COMMENTS_USER_AGENDA_QUERY, {
    variables: { documentId, siteId, userId: userInfo.userId },
    skip: !dirtyComment,
    onCompleted: data => {
      const newUserAgenda = handleAddNewUserAgenda(data.getCommentsUserAgenda, comment?.recipients);
      const updatedUserAgenda =
        newUserAgenda && newUserAgenda.length > 0 ? [...data.getCommentsUserAgenda, ...newUserAgenda] : data.getCommentsUserAgenda;
      (userAgendaLens |> set)(updatedUserAgenda);
    },
    fetchPolicy: 'network-only'
  });

  const handleAddNewUserAgenda = useCallback(
    (userAgenda, recipients) => {
      if (!recipients || recipients.length === 0) return;
      const newUserAgenda = [];
      const existingEmails = userAgenda.map(item => item.email);

      recipients.forEach(recipient => {
        const email = recipient.email;
        if (!existingEmails.includes(email)) {
          newUserAgenda.push({
            email: email,
            userId: userInfo.userId,
            contactName: recipient.contactName
          });
          existingEmails.push(email);
        }
      });

      return newUserAgenda;
    },
    [userInfo]
  );

  const handleResetComment = useCallback(() => {
    setComment(defaultComment);
    resetCommentLens(defaultComment);
    setDirtyComment(false);
  }, [resetCommentLens, setComment, setDirtyComment]);

  useEffect(() => {
    if (!(canSwitch |> get) && (comment === defaultComment || dirtyComment)) return;
    (commentLens |> set)(comment);
    (canSwitch |> set)(false);
    const updatedUserAgenda = userAgendaLens |> get;
    const newUserAgenda = handleAddNewUserAgenda(updatedUserAgenda, comment?.recipients);
    if (newUserAgenda?.length > 0) (userAgendaLens |> set)([...updatedUserAgenda, ...newUserAgenda]);
  }, [commentLens, currentComment, comment, dirtyComment, canSwitch, userAgendaLens, handleAddNewUserAgenda]);

  useEffect(() => {
    handleResetComment();
  }, [documentId, siteId, handleResetComment]);

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
    }
  });

  const handleAddRecipient = useCallback(
    value => {
      let validRecipients = [];
      const addedEmails = [];

      value.forEach(item => {
        let email = null;
        let newRecipient = null;

        if (typeof item === 'string') {
          email = item;
          if (validEmailRegEx.test(item)) {
            newRecipient = { email: item, contactName: null, userId: null, isStandardContact: false, userGroupId: null };
          }
        } else if (typeof item === 'object' && 'email' in item) {
          email = item.email;
          if (item.userGroupId != null) {
            newRecipient = {
              email: item.email,
              contactName: item.contactName,
              userId: item.userId,
              isStandardContact: item.isStandardContact,
              userGroupId: item.userGroupId
            };
          } else if (validEmailRegEx.test(item.email)) {
            newRecipient = {
              email: item.email,
              contactName: item.contactName,
              userId: item.userId,
              isStandardContact: item.isStandardContact,
              userGroupId: item.userGroupId
            };
          }
        }

        if (newRecipient && email && !addedEmails.includes(email.toLowerCase())) {
          validRecipients.push(newRecipient);
          addedEmails.push(email.toLowerCase());
        }
      });

      (commentLens.recipients |> set)(validRecipients);

      const userAgenda = userAgendaLens |> get;
      const newUserAgenda = handleAddNewUserAgenda(userAgenda, validRecipients);
      const updatedUserAgenda = newUserAgenda && newUserAgenda.length > 0 ? [...userAgenda, ...newUserAgenda] : userAgenda;
      (userAgendaLens |> set)(updatedUserAgenda);
    },
    [commentLens.recipients, handleAddNewUserAgenda, userAgendaLens]
  );

  const finishedSave = userId => {
    if (userInfo.userId === userId) {
      handleResetComment();
      setSaving(false);
      setDirtyComment(false);
    }
  };

  const [addOrUpdateComment] = useMutation(ADD_OR_UPDATE_COMMENT_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.addOrUpdateComment;
      storeKey({ correlationId });
    }
  });

  useGlobalSubscription(DOCUMENT_COMMENT_ADDED, {
    variables: {
      documentId,
      siteId,
      userPartnerId: userInfo.userExtraInfoModel.userPartnerId,
      commentIsDirty: dirtyComment,
      userId: userInfo.userId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      finishedSave(data.documentCommentAdded.userId);
    }
  });

  useGlobalSubscription(DOCUMENT_COMMENT_UPDATED, {
    variables: {
      documentId,
      siteId,
      userPartnerId: userInfo.userExtraInfoModel.userPartnerId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      finishedSave(data.documentCommentUpdated.userId);
    }
  });

  const handleSave = useCallback(() => {
    setSaving(true);
    if (!validateComment(currentComment)) {
      setSaving(false);
      return;
    }

    addOrUpdateComment({
      variables: {
        input: {
          documentId,
          siteId,
          commentId: currentComment.commentId,
          parentCommentId: currentComment.parentCommentId,
          commentText: currentComment.commentText,
          parentCommentText: currentComment.parentCommentText,
          internal: currentComment.internal,
          recipients: currentComment.recipients.filter(recipient => !recipient.isStandardContact).map(recipient => recipient.email),
          standardRecipients: currentComment.recipients
            .filter(recipient => recipient.isStandardContact)
            .map(recipient => ({
              userId: recipient.userId,
              userGroupId: recipient.userGroupId,
              recipient: recipient.userGroupId ? recipient.contactName : recipient.email
            }))
        }
      }
    });
    setDirtyComment(false);
  }, [validateComment, addOrUpdateComment, documentId, siteId, currentComment, setDirtyComment]);

  return (
    <AddCommentComponent
      saving={saving}
      commentLens={commentLens}
      onSave={handleSave}
      onAddRecipient={handleAddRecipient}
      onResetComment={handleResetComment}
      fullScreen={fullScreen}
      userAgendaLens={userAgendaLens}
      validationInfo={commentValidation}
      refetchComments={refetchComments}
      inputRef={inputRef}
      dirtyComment={dirtyComment}
      setDirtyComment={setDirtyComment}
    />
  );
}

AddCommentContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  comment: PropTypes.object.isRequired,
  setComment: PropTypes.func.isRequired,
  fullScreen: PropTypes.bool.isRequired,
  canSwitch: PropTypes.object.isRequired,
  setIsDirty: PropTypes.func.isRequired,
  refetchComments: PropTypes.func.isRequired,
  inputRef: PropTypes.object.isRequired,
  dirtyComment: PropTypes.bool.isRequired,
  setDirtyComment: PropTypes.func.IsRequired
};

export default AddCommentContainer;
